import { Grid, Alert } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { manageAccountIcon } from '../../assets/icons/icons';
import { useAuth } from '../../hooks/useAuth';
import {
  FetchChargerRequest,
  useFetchChargerDetailQuery,
  useFetchChargerNameByIdQuery,
} from '../../services/chargers.api';
import { useFetchListOfPayMethodsQuery } from '../../services/payment.api';
import { useFetchActivatedRFIDCardsQuery } from '../../services/rfid.api';
import { useLazyFetchTransactionByIdQuery } from '../../services/transactions.api';
import { useCreateTicketMethodMutation } from '../../services/zendesk.api';
import { useZendeskChatConfig } from '../../services/useZendeskChatConfig';
import { getTheme } from '../../stores/selectors/theme.selector';
import {
  createAuxiliarySupportCallTicketPayload,
  CreateZendeskTicketDto,
  ZENDESK_TICKET_TYPE,
} from '../../stores/types/zendesk.interface';
import { ConnectorStatus } from '../../types/connector-status.enum';
import { ROUTES } from '../../types/routes.enum';
import { BackButtonHeader } from '../ui-components/back-button-header.component';
import { LoadingDots } from '../ui-components/loading-dots/loading-dots.component';
import NetworkLogo from '../ui-components/network-logo.component';
import SignInSignUpButtons from '../ui-components/sign-in-sign-up-buttons.component';
import ChargerButtons from './charger-buttons.component';
import ChargerDescriptions from './charger-details/charger-descriptions.component';
import ChargerDetails from './charger-details/charger-details.component';
import { ChargerSessionTimeout } from './charger-session-timeout.component';
import { ChargerTimeToPlugin } from './charger-timeToPlugin.component';
import { usePostHog } from 'posthog-js/react';
import { usePostHogFeatureFlag } from '../../hooks/usePostHogFeatureFlag';
import { SessionFailed } from './session-failed.component';
import Banner from '../banner/banner.component';
import { AppBanner } from '../banner/app-banner.component';
import { UnlicensedPopup, RexelUnlicensedPopup } from './components/unlicensed-popup';

import { BUTTON_STATUS, RENDER_STATES } from './interfaces';
import {
  useSessionActions,
  useModalActions,
  useShouldDisplayLogin,
  useSessionUrl,
  useSessionById,
} from './hooks';
import { useFetchActiveSessionsQuery } from '../../services/sessions.api';
import ActiveSessionBanner from '../banner/active-session-banner.component';
import { Header } from '../ui-components/header.component';

export const Charger = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Session token state
  const auth = useAuth();
  const posthog = usePostHog();
  // URL parameter
  const { chargerName } = useParams();
  const [renderState, setRenderState] = useState(RENDER_STATES.SESSION_START);
  // Check the URL for a session ID
  const { sessionId, setSessionIdInUrl } = useSessionUrl();
  const [shouldPollActiveSessions, setShouldPollActiveSessions] = useState(false);

  // If there is no session ID in the URL, we need to fetch the active sessions for the driver
  const {
    data: activeSessionFromActiveSessions,
    isLoading: isLoadingActiveSessionFromActiveSessions,
  } = useFetchActiveSessionsQuery(undefined, {
    skip: sessionId !== null || !auth.isLoggedIn,
    pollingInterval: shouldPollActiveSessions ? 5000 : 0,
  });

  // If we get a active session back, we need to set the session ID in the URL
  useEffect(() => {
    if (activeSessionFromActiveSessions && activeSessionFromActiveSessions[0]?.sessionId) {
      setSessionIdInUrl(activeSessionFromActiveSessions[0]?.sessionId);
      setShouldPollActiveSessions(false);
    }
  }, [activeSessionFromActiveSessions, setSessionIdInUrl]);

  // If we get a session ID from the URL, we need to fetch the session by ID
  const { activeSession } = useSessionById(sessionId);

  const { data: chargerDetail, isSuccess } = useFetchChargerDetailQuery(
    {
      chargerName: chargerName,
      isLoggedIn: auth.isLoggedIn,
    } as FetchChargerRequest,
    {
      pollingInterval: 5000,
    },
  );

  const zendeskKey = useZendeskChatConfig(chargerDetail?.whiteLabellingId ?? null);

  // Fetch charger details for the other charger if session is on a different charger
  const { data: otherChargerNameWhereUserHasSession } = useFetchChargerNameByIdQuery(
    {
      chargerId: activeSession?.chargerId || '',
      isLoggedIn: auth.isLoggedIn,
    },
    {
      skip: !activeSession || !chargerDetail || activeSession.chargerId === chargerDetail.chargerId,
    },
  );

  const { shouldDisplayLogin } = useShouldDisplayLogin();
  const {
    startSession: startSessionAction,
    isStartSessionError,
    startSessionError,

    stopSession: stopSessionAction,
  } = useSessionActions();

  const [triggerCreateZendeskTicket] = useCreateTicketMethodMutation();
  const { showLogin, showRegister, handleIconClicked, handleZendeskChatClicked } =
    useModalActions(chargerName);
  const zendeskChatEnabled =
    usePostHogFeatureFlag('zendesk-ai-chat', false) && Boolean(zendeskKey) && auth.isLoggedIn;

  const { data: activatedCards } = useFetchActivatedRFIDCardsQuery(undefined, {
    skip: !auth.isLoggedIn,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showTechnicianIcon, setShowTechnicianIcon] = useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);
  const [transactionPollingInterval, setTransactionPollingInterval] = useState(0);
  const [transactionId, setTransactionId] = useState('');
  const [open, setOpen] = useState(false);
  const [unclaimedExperience, setUnclaimedExperience] = useState(null);
  const [displayBanner, setDisplayBanner] = useState(false);
  const [appBannerDisplay, setAppBannerDisplay] = useState(false);
  const [bannerPayload, setBannerPayload] = useState<any>();
  const [showCallbackSuccessAlert, setShowCallbackSuccessAlert] = useState(false);
  const currentUserEmail = auth?.user?.attributes?.email;
  const [showUnlicensedPopUpUserPreference, setShowUnlicensedPopUpUserPreference] =
    useState<boolean>(() => {
      const showPopupUserPreference =
        localStorage.getItem(`UnlicensedChargerShowPopup_${chargerName}-${currentUserEmail}`) || '';
      return showPopupUserPreference !== '' ? showPopupUserPreference === 'true' : true;
    });
  const [unlicensedCheckboxIsChecked, setUnlicensedCheckboxIsChecked] = useState(false);

  const [triggerFetchTransaction, { data: transaction, isSuccess: isFetchTransactionSuccess }] =
    useLazyFetchTransactionByIdQuery({
      pollingInterval: transactionPollingInterval,
    });

  useEffect(() => {
    if (posthog) {
      posthog.onFeatureFlags(function () {
        // feature flags should be available at this point
        if (posthog.isFeatureEnabled('webapp-site-banner')) {
          setBannerPayload(posthog.getFeatureFlagPayload('webapp-site-banner'));
          setDisplayBanner(true);
        }
        if (posthog.isFeatureEnabled('2025_AppInvitation')) {
          setAppBannerDisplay(true);
        }
      });
    }
  }, [posthog]);

  const handleBannerclose = () => {
    setDisplayBanner(false);
  };

  const handleAppBannerClose = () => {
    setAppBannerDisplay(false);
  };

  // When session start fails because of pre-auth
  // Stop loading & show error message and redirect to session failed page
  useEffect(() => {
    if (isStartSessionError) {
      // Capture the event where charging fails using PostHog
      posthog.capture('charging_failed', {
        method: 'charging',
        timestamp: new Date(), // Capture the timestamp of when the event occurred
      });

      setIsLoading(false);
      setRenderState(RENDER_STATES.SESSION_ERROR);
    }
  }, [isStartSessionError, posthog, setRenderState]);

  // Stop fetching transaction after successful fetch
  useEffect(() => {
    if (isFetchTransactionSuccess) {
      setTransactionPollingInterval(0);
    }
  }, [isFetchTransactionSuccess]);

  // Transaction id comes back on the session once the machine responds to startsession
  // Fetch Transaction only when session is stopped
  useEffect(() => {
    if (transactionId && activeSession.status !== 'CHARGING') {
      triggerFetchTransaction(transactionId);
    }
    if (transaction?.stopTime) {
      // Capture the session complete event using PostHog
      posthog.capture('session_complete', {
        method: 'charging',
        timestamp: new Date(), // Capture the timestamp of when the event occurred
      });
    }
  }, [transactionId, triggerFetchTransaction, transaction, posthog, activeSession]);

  useEffect(() => {
    if (isSuccess && !chargerDetail) {
      navigate(`/${ROUTES.FOUR_0_FOUR}`);
    }
  }, [isSuccess, chargerDetail, navigate]);

  useEffect(() => {
    if (currentUserEmail && chargerDetail) {
      const savedTechnicianViewValue =
        localStorage.getItem(`technicianView_${currentUserEmail}-${chargerDetail?.chargerId}`) ||
        '';

      setShowTechnicianIcon(savedTechnicianViewValue === 'true');
    }
  }, [currentUserEmail, chargerDetail]);

  // Handle callback success alert
  useEffect(() => {
    if (location.state?.callbackSuccess) {
      setShowCallbackSuccessAlert(true);
      // Clear the state after showing the alert
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.callbackSuccess, location.pathname, navigate]);

  useEffect(() => {
    const isAllConnectorAvailable = chargerDetail?.ports.every(
      (port) => port.status === 'AVAILABLE',
    );
    if (!isAllConnectorAvailable) {
      const availableConnector = chargerDetail?.ports.find((port) => port.status === 'AVAILABLE');
      setSelectedConnectorId(availableConnector?.portId);
    }
    // Open the pop up modal after checking header from chargerDetail response
    setOpen(chargerDetail?.siteHostSignUpWebLink ? true : false);
    // Determine open which pop up modal by checking header from chargerDetail response
    setUnclaimedExperience(chargerDetail?.siteHostSignUpChannelPartner);
  }, [chargerDetail]);

  const isChargingBySelf = useMemo(() => {
    return Boolean(activeSession);
  }, [activeSession]);

  useEffect(() => {
    if (activeSession) {
      setTransactionId(activeSession.transactionId);
    }
  }, [activeSession]);

  useEffect(() => {
    if (isLoading && isChargingBySelf) {
      setIsLoading(false);
      // Capture the charger charging status event using PostHog
      posthog.capture('charger_status', {
        method: 'charging',
        timestamp: new Date(), // Capture the timestamp of when the event occurred
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChargingBySelf]);

  const buttonStatus = useMemo(() => {
    // charger detail is not ready yet.
    if (!chargerDetail) return BUTTON_STATUS.INIT;
    if (isChargingBySelf) {
      return BUTTON_STATUS.IN_SESSION;
    } else if (chargerDetail.isAnyConnectorAvailable) {
      return BUTTON_STATUS.IDLE;
    }
  }, [chargerDetail, isChargingBySelf]);

  const handleConnectorChange = (connector) => {
    setSelectedConnectorId(connector);
  };

  // Charge session state
  // To determine if the stop button should be enabled / disabled when the charger is in use
  /* Default is the ChargeLab Theme, this will be updated based on the config json file */
  // The charger JSON config file should be externalized to the db level once the refactoring starts
  const theme = useSelector(getTheme);

  const { data: payment } = useFetchListOfPayMethodsQuery(undefined, {
    skip: !auth.isLoggedIn,
  });

  const clearTransactionId = () => {
    setTransactionId('');
  };

  const startCharge = () => {
    if (chargerDetail.isSingleConnector) {
      clearTransactionId();
      setIsLoading(true);
      setRenderState(RENDER_STATES.TIME_TO_PLUGIN);
      setShouldPollActiveSessions(true);
      startSessionAction({
        chargerId: chargerDetail.chargerId,
        portId: 1,
      });
    } else {
      const isConnectorAvailable = chargerDetail.ports.find(
        (port) =>
          port.portId === String(selectedConnectorId) && port.status === ConnectorStatus.AVAILABLE,
      );

      if (isConnectorAvailable) {
        clearTransactionId();
        setIsLoading(true);
        setRenderState(RENDER_STATES.TIME_TO_PLUGIN);
        setShouldPollActiveSessions(true);
        startSessionAction({
          chargerId: chargerDetail.chargerId,
          portId: selectedConnectorId,
        });
      }
    }
  };

  const handleSessionStopped = () => {
    if (isLoading) {
      setIsLoading(false);
    }
  };
  const stopCharge = async () => {
    if (activeSession.sessionId) {
      setTransactionId(activeSession.transactionId);
      setIsLoading(true);
      stopSessionAction(activeSession.sessionId);
    }
  };

  // Handles the click outside the modal
  const handleClose = () => {
    setOpen(true);
  };

  const handleSmartChargingBtnClick = () => {
    // When user is logged in set user preference to show popup based on 'don't show...' checkbox value
    auth.isLoggedIn &&
      setShowUnlicensedPopUpUserPreference(() => {
        localStorage.setItem(
          `UnlicensedChargerShowPopup_${chargerName}-${currentUserEmail}`,
          (!unlicensedCheckboxIsChecked).toString(),
        );
        return !unlicensedCheckboxIsChecked;
      });
    // Open sitehostsignupweblink in new broswer tab
    window.open(chargerDetail?.siteHostSignUpWebLink);
    //close the modal
    setOpen(false);
  };

  const handleViewChargerBtnClick = () => {
    // When user is logged in set user preference to show popup based on 'don't show...' checkbox value
    auth.isLoggedIn &&
      setShowUnlicensedPopUpUserPreference(() => {
        localStorage.setItem(
          `UnlicensedChargerShowPopup_${chargerName}-${currentUserEmail}`,
          (!unlicensedCheckboxIsChecked).toString(),
        );
        return !unlicensedCheckboxIsChecked;
      });
    //close the modal
    setOpen(false);
  };

  const createZendeskTicket = () => {
    try {
      if (auth.isLoggedIn) {
        const ticketDto: CreateZendeskTicketDto = {
          ticketType: ZENDESK_TICKET_TYPE.AUXILIARY_SUPPORT_CALL_TICKET,
          payload: createAuxiliarySupportCallTicketPayload(chargerDetail, payment, activatedCards),
        };
        triggerCreateZendeskTicket(ticketDto);
      }
    } catch (error) {
      // No matter what error occurs, we don't want to block the user from being able to call support
      // catch the error and do nothing
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setUnlicensedCheckboxIsChecked(checked);
  };

  useEffect(() => {
    if (renderState === RENDER_STATES.TIME_TO_PLUGIN && !isLoading) {
      setRenderState(RENDER_STATES.SESSION_START);
    }
  }, [isLoading, isStartSessionError, renderState]);

  if (!chargerDetail || !theme.primary || (isLoading && isLoadingActiveSessionFromActiveSessions)) {
    return (
      <Grid container justifyContent='center' alignItems='center'>
        <Grid item>
          <LoadingDots />
        </Grid>
      </Grid>
    );
  }

  const handleNavigateToOtherCharger = () => {
    if (otherChargerNameWhereUserHasSession) {
      // Navigate to the charger with the active session using charger name
      navigate(`/${otherChargerNameWhereUserHasSession}`);
    }
  };

  return (
    <div
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          width: '100%',
          maxWidth: 425,
          height: '100%',
          margin: 'auto',
        }}
      >
        {appBannerDisplay && <AppBanner onClose={handleAppBannerClose} />}
        {displayBanner && (
          <Banner
            type={bannerPayload?.type}
            message={bannerPayload?.message}
            url={bannerPayload?.url}
            onClose={handleBannerclose}
          />
        )}
        {showCallbackSuccessAlert && (
          <Alert
            severity='success'
            onClose={() => setShowCallbackSuccessAlert(false)}
            sx={{
              margin: '16px',
              borderRadius: '4px',
            }}
          >
            {t('callback_success_message')}
          </Alert>
        )}
        <Header
          onIconClicked={handleIconClicked}
          onZendeskChatClicked={zendeskChatEnabled ? handleZendeskChatClicked : null}
          icon={auth.isLoggedIn ? manageAccountIcon() : null}
          companyIcon={<NetworkLogo height='28px' width='auto' maxWidth={128} />}
        />

        {renderState === RENDER_STATES.SESSION_START && (
          <>
            <Grid
              container
              direction='column'
              style={{
                padding: '24px',
                backgroundColor: '#FFF',
                height: '100%',
              }}
            >
              {chargerDetail && (
                <ChargerDetails
                  charger={chargerDetail}
                  showTechnicianIcon={showTechnicianIcon}
                  buttonStatus={buttonStatus}
                  activeSession={otherChargerNameWhereUserHasSession ? undefined : activeSession}
                  transactionId={otherChargerNameWhereUserHasSession ? '' : transactionId}
                  onSessionStopped={handleSessionStopped}
                  handleConnectorChange={handleConnectorChange}
                  selectedConnectorId={selectedConnectorId}
                  transaction={otherChargerNameWhereUserHasSession ? undefined : transaction}
                  isChargingBySelf={isChargingBySelf}
                  stopCharge={stopCharge}
                  loading={isLoading}
                />
              )}
              {!transactionId && (
                <ChargerDescriptions
                  charger={chargerDetail}
                  themeColor={theme.primary}
                  isLoggedIn={auth.isLoggedIn}
                  stopCharge={stopCharge}
                  isChargingBySelf={isChargingBySelf}
                  activeSession={activeSession}
                  createZendeskTicket={createZendeskTicket}
                />
              )}
            </Grid>
            {shouldDisplayLogin && (
              <SignInSignUpButtons showLogin={showLogin} showRegister={showRegister} />
            )}
            {/* Show stop buttons only when session is on current charger */}
            {!(transactionId && transaction) &&
              !isChargingBySelf &&
              !otherChargerNameWhereUserHasSession && (
                <ChargerButtons
                  startCharge={startCharge}
                  stopCharge={stopCharge}
                  charger={chargerDetail}
                  buttonStatus={buttonStatus}
                  loading={isLoading}
                  showTechView={showTechnicianIcon}
                  isChargingBySelf={isChargingBySelf}
                  selectedConnectorId={selectedConnectorId}
                  activeSession={activeSession}
                />
              )}
            {/* Banner for ongoing session on different charger */}
            {otherChargerNameWhereUserHasSession && activeSession && chargerDetail && (
              <ActiveSessionBanner
                chargerName={otherChargerNameWhereUserHasSession}
                onNavigate={handleNavigateToOtherCharger}
              />
            )}
          </>
        )}
        {renderState === RENDER_STATES.TIME_TO_PLUGIN && isLoading && (
          <ChargerTimeToPlugin
            charger={chargerDetail}
            selectedConnectorId={selectedConnectorId}
            sessionError={() => {
              setIsLoading(false);
              setRenderState(RENDER_STATES.SESSION_ERROR);
            }}
            sessionTimeout={() => {
              setIsLoading(false);
              setRenderState(RENDER_STATES.SESSION_TIMEOUT);
            }}
          />
        )}
        {renderState === RENDER_STATES.SESSION_TIMEOUT && (
          <ChargerSessionTimeout
            charger={chargerDetail}
            selectedConnectorId={selectedConnectorId}
            onRetry={startCharge}
            onCancel={() => {
              setRenderState(RENDER_STATES.SESSION_START);
            }}
          />
        )}
        {renderState === RENDER_STATES.SESSION_ERROR && (
          <SessionFailed
            charger={chargerDetail}
            selectedConnectorId={selectedConnectorId}
            onRetry={startCharge}
            onCancel={() => {
              setRenderState(RENDER_STATES.SESSION_START);
            }}
            errorCode={(startSessionError as any)?.data?.errorCode}
          />
        )}
      </div>
      {showUnlicensedPopUpUserPreference &&
        (unclaimedExperience !== 'REXEL' ? (
          <UnlicensedPopup
            doNotShowAgainCheckbox={auth.isLoggedIn}
            handleCheckboxChange={handleCheckboxChange}
            handleClose={handleClose}
            handleSmartChargingBtnClick={handleSmartChargingBtnClick}
            handleViewChargerBtnClick={handleViewChargerBtnClick}
            open={open}
            checked={unlicensedCheckboxIsChecked}
          />
        ) : (
          <RexelUnlicensedPopup
            doNotShowAgainCheckbox={auth.isLoggedIn}
            handleCheckboxChange={handleCheckboxChange}
            handleClose={handleClose}
            handleSmartChargingBtnClick={handleSmartChargingBtnClick}
            handleViewChargerBtnClick={handleViewChargerBtnClick}
            open={open}
            checked={unlicensedCheckboxIsChecked}
          />
        ))}
    </div>
  );
};
